import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { useCuerpo } from "../context/CuerpoContext";
import "./GestionOrganigrama.css";

const VACIO = {
  titulo: "",
  descripcion: "",
  tipo: "cargo",
  parent_id: "",
  orden: 1,
  asignacion_tipo: "cargo",
  cargo_busqueda: "",
  bombero_id: "",
  compania_persona: "",
  mostrar_compania: true,
};

function ordenar(a, b) {
  const ao = Number(a?.orden ?? 999999);
  const bo = Number(b?.orden ?? 999999);
  if (ao !== bo) return ao - bo;
  return String(a?.titulo || "").localeCompare(String(b?.titulo || ""), "es");
}

function norm(v) {
  return String(v || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function rutaNodo(nodo, mapa) {
  if (!nodo) return "";
  const partes = [];
  const visitados = new Set();
  let actual = nodo;

  while (actual && !visitados.has(actual.id)) {
    visitados.add(actual.id);
    partes.unshift(actual.titulo || "Sin nombre");
    actual = actual.parent_id ? mapa.get(actual.parent_id) : null;
  }

  return partes.join(" › ");
}

function obtenerDescendientes(id, nodos) {
  const salida = new Set();
  const pendientes = [id];

  while (pendientes.length) {
    const actual = pendientes.shift();
    nodos.forEach((n) => {
      if (n.parent_id === actual && !salida.has(n.id)) {
        salida.add(n.id);
        pendientes.push(n.id);
      }
    });
  }

  return salida;
}

export default function GestionOrganigrama() {
  const { cuerpo, cuerpoId } = useCuerpo();
  const [nodos, setNodos] = useState([]);
  const [bomberos, setBomberos] = useState([]);
  const [form, setForm] = useState(VACIO);
  const [editandoId, setEditandoId] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!cuerpoId) return undefined;

    const unsubNodos = onSnapshot(
      query(collection(db, "organigrama_nodos"), where("cuerpo_id", "==", cuerpoId)),
      (snap) => setNodos(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(ordenar)),
      (e) => setError(e.message || "No se pudo cargar el organigrama")
    );

    const unsubBomberos = onSnapshot(
      query(collection(db, "bomberos"), where("cuerpo_id", "==", cuerpoId)),
      (snap) => setBomberos(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || ""), "es"))
      ),
      (e) => setError(e.message || "No se pudo cargar el personal")
    );

    return () => {
      unsubNodos();
      unsubBomberos();
    };
  }, [cuerpoId]);

  useEffect(() => {
    setForm(VACIO);
    setEditandoId(null);
  }, [cuerpoId]);

  const cargos = useMemo(() => {
    const mapa = new Map();
    bomberos.forEach((b) => {
      const cargo = String(b.cargo || "").trim();
      if (cargo && !mapa.has(norm(cargo))) mapa.set(norm(cargo), cargo);
    });
    return [...mapa.values()].sort((a, b) => a.localeCompare(b, "es"));
  }, [bomberos]);

  const companiasPersonal = useMemo(() => {
    const mapa = new Map();
    bomberos.forEach((b) => {
      const compania = String(b.compania || b.companiaNombre || "").trim();
      if (compania && !mapa.has(norm(compania))) mapa.set(norm(compania), compania);
    });
    return [...mapa.values()].sort((a, b) => a.localeCompare(b, "es"));
  }, [bomberos]);

  const bomberosFiltradosPersona = useMemo(() => {
    if (!form.compania_persona) return [];
    const buscada = norm(form.compania_persona);
    return bomberos.filter((b) =>
      norm(b.compania || b.companiaNombre || "") === buscada
    );
  }, [bomberos, form.compania_persona]);

  const nodosMap = useMemo(() => new Map(nodos.map((n) => [n.id, n])), [nodos]);

  const bomberosMap = useMemo(() => {
    const mapa = new Map();
    bomberos.forEach((b) => {
      [b.id, b.rut, b.uid].filter(Boolean).forEach((k) => mapa.set(String(k), b));
    });
    return mapa;
  }, [bomberos]);

  const companiaDesdeRuta = (ruta) => {
    const r = norm(ruta);
    if (!r) return "";
    const ordenadas = [...companiasPersonal].sort((a, b) => norm(b).length - norm(a).length);
    return ordenadas.find((c) => r.includes(norm(c))) || "";
  };

  const etiquetaPadre = (n) => {
    const ruta = rutaNodo(n, nodosMap);
    if (n.tipo === "organismo") return `${ruta} [Organismo]`;

    const detalles = [];
    let persona = null;

    if (n.asignacion_tipo === "persona" && n.bombero_id) {
      persona = bomberosMap.get(String(n.bombero_id)) || null;
    }

    if (persona) {
      const compania = String(persona.compania || persona.companiaNombre || persona.compañia || "").trim();
      if (compania) detalles.push(compania);
      if (persona.nombre) detalles.push(persona.nombre);
    } else {
      const companiaRuta = companiaDesdeRuta(ruta);
      if (companiaRuta) detalles.push(companiaRuta);
      if (n.asignacion_tipo === "cargo" && n.cargo_busqueda) detalles.push(`Automático: ${n.cargo_busqueda}`);
      if (n.asignacion_tipo === "vacante") detalles.push("Vacante");
    }

    // Si todavía hay dos cargos idénticos, el orden ayuda a distinguirlos.
    detalles.push(`orden ${Number(n.orden || 1)}`);

    return `${ruta} [Cargo${detalles.length ? ` · ${detalles.join(" · ")}` : ""}]`;
  };

  const descendientesEditando = useMemo(
    () => editandoId ? obtenerDescendientes(editandoId, nodos) : new Set(),
    [editandoId, nodos]
  );

  const padresDisponibles = useMemo(() => {
    return nodos
      .filter((n) => n.id !== editandoId && !descendientesEditando.has(n.id))
      .map((n) => ({ ...n, ruta: rutaNodo(n, nodosMap) }))
      .sort((a, b) => a.ruta.localeCompare(b.ruta, "es"));
  }, [nodos, editandoId, descendientesEditando, nodosMap]);

  const hijosCount = useMemo(() => {
    const m = new Map();
    nodos.forEach((n) => {
      if (n.parent_id) m.set(n.parent_id, (m.get(n.parent_id) || 0) + 1);
    });
    return m;
  }, [nodos]);

  const cambiar = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  const cancelar = () => {
    setForm(VACIO);
    setEditandoId(null);
    setError("");
  };

  const editar = (n) => {
    setEditandoId(n.id);
    setForm({
      titulo: n.titulo || "",
      descripcion: n.descripcion || "",
      tipo: n.tipo || "cargo",
      parent_id: n.parent_id || "",
      orden: Number(n.orden || 1),
      asignacion_tipo: n.tipo === "organismo" ? "ninguna" : (n.asignacion_tipo || "cargo"),
      cargo_busqueda: n.cargo_busqueda || "",
      bombero_id: n.bombero_id || "",
      compania_persona: (() => {
        const b = bomberos.find((x) =>
          String(x.id) === String(n.bombero_id || "") ||
          String(x.rut || "") === String(n.bombero_id || "")
        );
        return b ? String(b.compania || b.companiaNombre || "").trim() : "";
      })(),
      // Los nodos existentes siguen mostrando compañía por compatibilidad.
      mostrar_compania: n.mostrar_compania !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const guardar = async () => {
    if (!cuerpoId) return;
    if (!form.titulo.trim()) return setError("Debes indicar el nombre del cargo u organismo.");
    if (form.parent_id && form.parent_id === editandoId) return setError("Un nodo no puede depender de sí mismo.");
    if (editandoId && form.parent_id && descendientesEditando.has(form.parent_id)) {
      return setError("No puedes hacer que un nodo dependa de uno de sus propios descendientes.");
    }
    if (form.tipo === "cargo" && form.asignacion_tipo === "cargo" && !form.cargo_busqueda) {
      return setError("Selecciona el cargo que se asignará automáticamente.");
    }
    if (form.tipo === "cargo" && form.asignacion_tipo === "persona" && !form.compania_persona) {
      return setError("Selecciona primero la compañía de la persona.");
    }
    if (form.tipo === "cargo" && form.asignacion_tipo === "persona" && !form.bombero_id) {
      return setError("Selecciona una persona.");
    }

    setGuardando(true);
    setError("");

    const data = {
      cuerpo_id: cuerpoId,
      titulo: form.titulo.trim(),
      descripcion: form.descripcion.trim(),
      tipo: form.tipo,
      parent_id: form.parent_id || null,
      orden: Number(form.orden || 1),
      asignacion_tipo: form.tipo === "organismo" ? "ninguna" : form.asignacion_tipo,
      cargo_busqueda: form.tipo === "cargo" && form.asignacion_tipo === "cargo" ? form.cargo_busqueda : "",
      bombero_id: form.tipo === "cargo" && form.asignacion_tipo === "persona" ? form.bombero_id : "",
      mostrar_compania: form.tipo === "cargo" ? form.mostrar_compania !== false : false,
      actualizado_en: serverTimestamp(),
    };

    try {
      if (editandoId) {
        await updateDoc(doc(db, "organigrama_nodos", editandoId), data);
      } else {
        await addDoc(collection(db, "organigrama_nodos"), { ...data, creado_en: serverTimestamp() });
      }
      cancelar();
    } catch (e) {
      console.error(e);
      setError(e.message || "No se pudo guardar el nodo.");
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (n) => {
    const cantHijos = hijosCount.get(n.id) || 0;
    const detalle = cantHijos > 0
      ? `\n\nTiene ${cantHijos} nodo(s) hijo(s). Al eliminarlo, esos nodos subirán al nivel de su padre.`
      : "";

    if (!window.confirm(`¿Eliminar "${n.titulo}"?${detalle}`)) return;

    try {
      if (cantHijos > 0) {
        const batch = writeBatch(db);
        nodos.filter((x) => x.parent_id === n.id).forEach((h) => {
          batch.update(doc(db, "organigrama_nodos", h.id), {
            parent_id: n.parent_id || null,
            actualizado_en: serverTimestamp(),
          });
        });
        batch.delete(doc(db, "organigrama_nodos", n.id));
        await batch.commit();
      } else {
        await deleteDoc(doc(db, "organigrama_nodos", n.id));
      }

      if (editandoId === n.id) cancelar();
    } catch (e) {
      console.error(e);
      setError(e.message || "No se pudo eliminar el nodo.");
    }
  };

  const etiquetaAsignacion = (n) => {
    if (n.tipo === "organismo") return "Organismo";
    if (n.asignacion_tipo === "persona") {
      const b = bomberos.find((x) => String(x.id) === String(n.bombero_id) || String(x.rut || "") === String(n.bombero_id));
      return b ? `Persona: ${b.nombre}` : "Persona específica";
    }
    if (n.asignacion_tipo === "vacante") return "Vacante";
    return `Automático: ${n.cargo_busqueda || n.titulo}`;
  };

  return (
    <div className="go-wrap">
      <h2>Organigrama</h2>
      <p className="go-muted">
        Configuración de <strong>{cuerpo?.nombre || cuerpoId}</strong>. El organigrama público se actualiza automáticamente con los datos del personal.
      </p>

      <div className="go-form-card">
        <h3>{editandoId ? "Editar nodo" : "Agregar nodo"}</h3>
        {error && <div className="go-error">{error}</div>}

        <div className="go-grid">
          <label>
            Tipo
            <select
              value={form.tipo}
              onChange={(e) => {
                const tipo = e.target.value;
                setForm((f) => ({
                  ...f,
                  tipo,
                  asignacion_tipo: tipo === "organismo"
                    ? "ninguna"
                    : (f.asignacion_tipo === "ninguna" ? "cargo" : f.asignacion_tipo),
                }));
              }}
            >
              <option value="cargo">Cargo</option>
              <option value="organismo">Organismo</option>
            </select>
          </label>

          <label>
            Nombre
            <input
              value={form.titulo}
              onChange={(e) => cambiar("titulo", e.target.value)}
              placeholder={form.tipo === "cargo" ? "Ej. Comandante" : "Ej. Directorio General"}
            />
          </label>

          <label>
            Depende de
            <select value={form.parent_id} onChange={(e) => cambiar("parent_id", e.target.value)}>
              <option value="">— Nivel principal —</option>
              {padresDisponibles.map((n) => (
                <option key={n.id} value={n.id}>
                  {etiquetaPadre(n)}
                </option>
              ))}
            </select>
            <span className="go-small">
              Los cargos muestran también compañía y persona asignada para distinguir nodos con el mismo nombre.
            </span>
          </label>

          <label>
            Orden
            <input type="number" min="1" value={form.orden} onChange={(e) => cambiar("orden", e.target.value)} />
          </label>
        </div>

        <label className="go-full">
          Descripción opcional
          <input
            value={form.descripcion}
            onChange={(e) => cambiar("descripcion", e.target.value)}
            placeholder="Texto breve bajo el nodo"
          />
        </label>

        {form.tipo === "cargo" && (
          <div className="go-assignment">
            <label>
              Asignación
              <select
                value={form.asignacion_tipo}
                onChange={(e) => {
                  const tipoAsignacion = e.target.value;
                  setForm((f) => ({
                    ...f,
                    asignacion_tipo: tipoAsignacion,
                    compania_persona: tipoAsignacion === "persona" ? f.compania_persona : "",
                    bombero_id: tipoAsignacion === "persona" ? f.bombero_id : "",
                  }));
                }}
              >
                <option value="cargo">Automática por cargo</option>
                <option value="persona">Persona específica</option>
                <option value="vacante">Vacante</option>
              </select>
            </label>

            {form.asignacion_tipo === "cargo" && (
              <label>
                Cargo existente
                <select value={form.cargo_busqueda} onChange={(e) => cambiar("cargo_busqueda", e.target.value)}>
                  <option value="">— Seleccionar cargo —</option>
                  {cargos.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            )}

            {form.asignacion_tipo === "persona" && (
              <>
                <label>
                  Compañía
                  <select
                    value={form.compania_persona}
                    onChange={(e) => {
                      const compania = e.target.value;
                      setForm((f) => ({
                        ...f,
                        compania_persona: compania,
                        bombero_id: "",
                      }));
                    }}
                  >
                    <option value="">— Seleccionar compañía —</option>
                    {companiasPersonal.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </label>

                <label>
                  Bombero
                  <select
                    value={form.bombero_id}
                    onChange={(e) => cambiar("bombero_id", e.target.value)}
                    disabled={!form.compania_persona}
                  >
                    <option value="">
                      {form.compania_persona ? "— Seleccionar persona —" : "— Selecciona primero la compañía —"}
                    </option>
                    {bomberosFiltradosPersona.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.nombre} · {b.cargo || "Sin cargo"}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}

            <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
              <input
                type="checkbox"
                checked={form.mostrar_compania !== false}
                onChange={(e) => cambiar("mostrar_compania", e.target.checked)}
                style={{ width: "auto" }}
              />
              <span>Mostrar compañía en la tarjeta pública</span>
            </label>
          </div>
        )}

        <div className="go-actions">
          <button className="go-primary" onClick={guardar} disabled={guardando}>
            {guardando ? "Guardando..." : (editandoId ? "Guardar cambios" : "Agregar nodo")}
          </button>
          {editandoId && <button onClick={cancelar}>Cancelar</button>}
        </div>
      </div>

      <div className="go-list-card">
        <h3>Estructura configurada</h3>
        {nodos.length === 0 ? (
          <p className="go-muted">Aún no existen nodos. Comienza creando el nivel superior del organigrama.</p>
        ) : (
          <>
            <section style={{ marginTop: 18, marginBottom: 34 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
                <h4 style={{ margin: 0, fontSize: "1.15rem" }}>🏛️ Organismos</h4>
                <span className="go-small">{nodos.filter((n) => n.tipo === "organismo").length} configurado(s)</span>
              </div>

              {nodos.filter((n) => n.tipo === "organismo").length === 0 ? (
                <p className="go-muted">No hay organismos configurados.</p>
              ) : (
                <div className="go-table-wrap">
                  <table className="go-table">
                    <thead>
                      <tr>
                        <th>Orden</th><th>Nombre</th><th>Depende de</th><th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nodos
                        .filter((n) => n.tipo === "organismo")
                        .slice()
                        .sort((a, b) => {
                          const ra = rutaNodo(a, nodosMap);
                          const rb = rutaNodo(b, nodosMap);
                          const cmp = ra.localeCompare(rb, "es");
                          return cmp !== 0 ? cmp : ordenar(a, b);
                        })
                        .map((n) => {
                          const padre = nodosMap.get(n.parent_id);
                          return (
                            <tr key={n.id}>
                              <td>{n.orden ?? ""}</td>
                              <td>
                                <strong>{n.titulo}</strong>
                                {n.descripcion ? <div className="go-small">{n.descripcion}</div> : null}
                              </td>
                              <td>{padre ? rutaNodo(padre, nodosMap) : "Nivel principal"}</td>
                              <td className="go-row-actions">
                                <button onClick={() => editar(n)}>Editar</button>
                                <button className="go-danger" onClick={() => eliminar(n)}>Eliminar</button>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
                <h4 style={{ margin: 0, fontSize: "1.15rem" }}>👤 Cargos</h4>
                <span className="go-small">{nodos.filter((n) => n.tipo !== "organismo").length} configurado(s)</span>
              </div>

              {nodos.filter((n) => n.tipo !== "organismo").length === 0 ? (
                <p className="go-muted">No hay cargos configurados.</p>
              ) : (
                <div className="go-table-wrap">
                  <table className="go-table">
                    <thead>
                      <tr>
                        <th>Orden</th><th>Nombre</th><th>Depende de</th><th>Asignación</th><th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nodos
                        .filter((n) => n.tipo !== "organismo")
                        .slice()
                        .sort((a, b) => {
                          const pa = a.parent_id ? rutaNodo(nodosMap.get(a.parent_id), nodosMap) : "";
                          const pb = b.parent_id ? rutaNodo(nodosMap.get(b.parent_id), nodosMap) : "";
                          const cmpPadre = pa.localeCompare(pb, "es");
                          return cmpPadre !== 0 ? cmpPadre : ordenar(a, b);
                        })
                        .map((n) => {
                          const padre = nodosMap.get(n.parent_id);
                          return (
                            <tr key={n.id}>
                              <td>{n.orden ?? ""}</td>
                              <td>
                                <strong>{n.titulo}</strong>
                                {n.descripcion ? <div className="go-small">{n.descripcion}</div> : null}
                              </td>
                              <td>{padre ? rutaNodo(padre, nodosMap) : "Nivel principal"}</td>
                              <td>{etiquetaAsignacion(n)}</td>
                              <td className="go-row-actions">
                                <button onClick={() => editar(n)}>Editar</button>
                                <button className="go-danger" onClick={() => eliminar(n)}>Eliminar</button>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
